import { useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import moment from 'moment-timezone';
import { CustomSnackbarProvider } from '@mgm/common/providers';
import { useTranslation } from 'react-i18next';
import { InitRoutes } from './router';
import ThemeConfig from './theme';
import { useAuth } from './hooks/useAuth';
import { PWAUpdateDialog } from './components';
import { useAppDispatch, useAppSelector } from './hooks/useAppStore';
import { getIsOpenPagesNav } from './store/common/selectors';
import { commonActions } from './store/common/slice';
import { TItemChildren } from './components/base/BList/type';
import { getActiveStep } from './store/payroll/selectors';
import { payrollActions } from './store/payroll/slice';
import './assets/css/print.css';
import { helpActions } from './store/help/slice';
import { TSelectedArticle } from './types';
import { tasklistsActions } from './store/tasklists/slice';
import { commissionsActions } from './store/commissions/slice';
import { getIsOpenAssignMembersNav } from './store/commissions/selectors';
import { ordersActions } from './store/orders/slice';
import { settingsActions } from './store/settings/slice';
import { inventoryActions } from './store/inventory/slice';
import { getAuthenticateUser, getTokenDetails } from './store/authenticate/selectors';
import { getSelectedLocationId } from './store/inventory/selector';
import { useCommon } from './hooks/useCommon';

import 'react-quill/dist/quill.snow.css';
import 'simplebar-react/dist/simplebar.min.css';
import { setPreviousPath } from './utils/helpers/routeTracker';

function App() {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const isOpenPagesNav = useAppSelector(getIsOpenPagesNav);
  const isOpenAssignMembersNav = useAppSelector(getIsOpenAssignMembersNav);
  const payrollActiveStep = useAppSelector(getActiveStep);
  const { shop } = useAppSelector(getAuthenticateUser);
  const selectedLocationId = useAppSelector(getSelectedLocationId);
  const tokenDetails = useAppSelector(getTokenDetails);
  const { locationsByPermissions } = useCommon();
  const { isLoggedIn } = useAuth();

  const { t } = useTranslation('dashboard');

  useEffect(() => () => {
    setPreviousPath(location.pathname);
    if (payrollActiveStep > 0) {
      dispatch(payrollActions.setActiveStep(0));
    }
    if (isOpenPagesNav) {
      dispatch(commonActions.setIsOpenPagesNav(false));
    }
    if (isOpenAssignMembersNav) {
      dispatch(commissionsActions.setIsOpenAssignMembersNav(false));
    }
    dispatch(commonActions.selectedFilterItems({} as { [key: string]: Array<TItemChildren> }));
    dispatch(helpActions.setSelectedArticle({} as TSelectedArticle));
    dispatch(tasklistsActions.setIsOpenAllTasklistsNav(false));

    if (location.pathname !== '/team' && location.pathname !== '/team/commission-model') dispatch(commissionsActions.setIsOpenTeamCommissionsNav(false));
    dispatch(settingsActions.setIsOpenIntegrationNav(false));

    dispatch(ordersActions.setDorChosenDate(moment.tz(shop?.shopTimezone).startOf('day').format()));

    dispatch(ordersActions.setOrdersChosenDates([
      moment.tz(shop?.shopTimezone).startOf('week').startOf('day'),
      moment.tz(shop?.shopTimezone).endOf('week').endOf('day'),
    ]));

    dispatch(ordersActions.setMenuOptionName(t('lastSevenDays')));
    dispatch(ordersActions.setDailyStateSelectedLocation(0));

    if (shop?.locationsData) {
      shop.locationsData?.filter((location) => location.id > 1).forEach((location) => {
        dispatch(inventoryActions.setInventoryLocation(location.id));
      });
    }

    if (selectedLocationId === null) {
      dispatch(inventoryActions.setSelectedLocationId(Number(locationsByPermissions?.[1]?.id)));
    }
  }, [location]);

  return (
    <CustomSnackbarProvider userTheme={tokenDetails?.settings?.ui?.theme}>
      <ThemeConfig>
        <InitRoutes key={location.pathname} />
        {isLoggedIn() && <PWAUpdateDialog />}
      </ThemeConfig>
    </CustomSnackbarProvider>
  );
}

export default Sentry.withProfiler(App);
